import { endpoints } from '@/services/endpoints'
import { get } from '@/services/http'
import { MENTION_MODULES } from './pageContext'

/**
 * Typing `@` and `#` in the composer.
 *
 * `@` names a module — `@Logbook` — and `#` names a record — `#LOG-26`. Both are
 * inserted into the text as the reader sees them and remembered beside it with
 * what they mean; at send, a mention whose label is no longer in the text was
 * deleted by the reader and is dropped. So editing the sentence is how a mention
 * is removed, the same as in any chat that has them.
 */

/**
 * The `@…` or `#…` being typed at the caret, or null.
 *
 * Only at the start of the text or after whitespace, so an email address or an
 * `ANM#3` inside a word is not a trigger. The query stops at whitespace: a mention
 * label is one token while it is being typed.
 */
export function activeTrigger(text, caret) {
  const before = text.slice(0, caret)
  const match = before.match(/(^|\s)([@#])([^\s@#]{0,40})$/)
  if (!match) return null
  return { kind: match[2], query: match[3], start: caret - match[3].length - 1, end: caret }
}

/** The `@` menu: modules whose label starts with — or contains — what was typed. */
export function matchModules(query, labelOf) {
  const needle = query.trim().toLowerCase()
  const all = MENTION_MODULES.map((module) => ({ module, module_id: null, label: labelOf(module) }))
  if (!needle) return all
  const starts = all.filter((entry) => entry.label.toLowerCase().startsWith(needle))
  const contains = all.filter(
    (entry) => !entry.label.toLowerCase().startsWith(needle) && entry.label.toLowerCase().includes(needle),
  )
  return [...starts, ...contains]
}

/**
 * Where `#` looks, and how a row is named.
 *
 * Every list here takes `search` server-side — serial and title where a record has
 * a serial, name where it has a name. `label` is what goes into the text after the
 * `#`, so it must be one token: a serial is; a name may not be, and its spaces are
 * replaced. `detail` is the second line in the menu.
 */
const SOURCES = [
  {
    module: 'anomalies',
    path: endpoints.anomalies.list,
    key: 'anomalies',
    label: (row) => row.serial_number,
    detail: (row) => row.title,
  },
  {
    module: 'work_orders',
    path: endpoints.workOrders.list,
    key: 'work_orders',
    label: (row) => row.serial_number,
    detail: (row) => row.title,
  },
  {
    module: 'logbook',
    path: endpoints.logbook.list,
    key: 'logbook_entries',
    label: (row) => row.serial_number,
    detail: (row) => row.title,
  },
  {
    module: 'shift_handovers',
    path: endpoints.shiftHandovers.list,
    key: 'shift_handovers',
    label: (row) => row.serial_number,
    detail: (row) => row.notes,
  },
  { module: 'sensors', path: endpoints.sensors.list, key: 'sensors', label: (row) => row.name },
  { module: 'documents', path: endpoints.documents.list, key: 'documents', label: (row) => row.name },
  { module: 'assets', path: endpoints.hierarchy.machines, key: 'machines', label: (row) => row.name },
  { module: 'assets', path: endpoints.hierarchy.lines, key: 'lines', label: (row) => row.name },
  { module: 'assets', path: endpoints.hierarchy.plants, key: 'plants', label: (row) => row.name },
  { module: 'shifts', path: endpoints.shifts.patterns, key: 'shifts', label: (row) => row.name },
]

const PER_SOURCE = 3

/** One token, so the label survives being matched back out of the text at send. */
export const mentionToken = (label) => String(label ?? '').trim().replace(/\s+/g, '-')

/**
 * Records matching `query`, across every module, grouped in `SOURCES` order.
 *
 * `allSettled`, not `all`: a list this person may not read answers 403, and that
 * is an ordinary outcome here — it means "nothing from that module", not a
 * failure of the search.
 */
export async function searchRecords(query, signal) {
  const results = await Promise.allSettled(
    SOURCES.map((source) =>
      get(source.path, { signal, params: { search: query, page: 1, page_size: PER_SOURCE } }).then((body) =>
        (body?.[source.key] ?? [])
          .map((row) => ({
            module: source.module,
            module_id: row.id,
            label: mentionToken(source.label(row)),
            detail: source.detail?.(row) ?? null,
          }))
          .filter((entry) => entry.module_id && entry.label),
      ),
    ),
  )
  return results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
}

/**
 * The mentions a message still carries.
 *
 * `tracked` is everything the reader picked from a menu. One whose `@label` or
 * `#label` is no longer a whole token in the text was edited away, and is dropped.
 */
export function mentionsInText(text, tracked) {
  return tracked.filter((mention) => {
    const token = `${mention.module_id ? '#' : '@'}${mention.label}`
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(`(^|\\s)${escaped}(?=\\s|$|[.,;:!?])`).test(text)
  })
}
